import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";
import {
  useAdminFirmApplicationDetailsQuery,
  useUpdateFirmApplicationStatusMutation,
  useGetFirmCommitteePoolQuery,
  useGetFirmCommitteeReviewsQuery,
  useAssignFirmCommitteeMutation,
  useUnassignFirmCommitteeMutation,
  useGetNextFirmNumberQuery,
  useIssueFirmNumberMutation,
  useSetFirmDonationMutation,
} from "../api/firmApi";
import FirmStatusBadge from "../components/FirmStatusBadge";
import { firmStorageUrl } from "../utils";
import { describeApiError } from "../../../lib/apiError";
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";

const decisionIcon: Record<string, JSX.Element> = {
  accepted: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  approved: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  pending: <Clock className="w-4 h-4 text-amber-500" />,
  declined: <XCircle className="w-4 h-4 text-red-500" />,
  rejected: <XCircle className="w-4 h-4 text-red-500" />,
};

/**
 * What the SECRETARIAT may do, which is far less than it used to be.
 *
 * Approving and rejecting belong to the Membership Committee now (assign below,
 * which hands the application over — that is what moves it out of Under Review
 * and into With Committee — and the assigned member decides).
 * `awaiting_endorsement → under_review` happens by itself once the referees and
 * the chapter have signed off. The two
 * steps after the committee are not status changes either, and have their own
 * panels: recording the donation agreed at the interview, and issuing the firm
 * number, which is permanent.
 *
 * What is left here is handing an application back to the applicant.
 */
const TRANSITIONS: Record<string, string[]> = {
  under_review: ["returned"],
  with_committee: ["returned"],
  rejected: ["returned"],
};

const ACTION_LABEL: Record<string, string> = {
  returned: "Return to applicant",
};

function Row({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{String(value)}</span>
    </div>
  );
}

/** A stored date, as a reader wants it. Blank stays blank. */
function fmtDate(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-US", { dateStyle: "medium" });
}

function PartnerCard({ p, principal }: { p: any; principal?: boolean }) {
  const cv = firmStorageUrl(p.cv_file);
  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900">{p.full_name ?? p.member?.surname ?? p.member_id}</p>
          <p className="text-xs text-gray-400">
            {principal ? <span className="text-indigo-600">Principal partner · </span> : null}
            {[
              p.previous_name && `previously ${p.previous_name}`,
              p.age && `age ${p.age}`,
              p.niqs_membership_no && `NIQS ${p.niqs_membership_no}`,
              p.niqs_election_date && `elected ${String(p.niqs_election_date).slice(0, 10)}`,
              p.qsrbn_reg_date && `QSRBN registered ${String(p.qsrbn_reg_date).slice(0, 10)}`,
            ].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cv && <a href={cv} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">CV</a>}
          {/* Declared on the form rather than invited from the register — only
              somebody with a member_id ever had anything to accept. */}
          {p.member_id && decisionIcon[p.decision]}
        </div>
      </div>
      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
        {p.academic_qualification && <div><dt className="text-gray-400">Academic</dt><dd>{p.academic_qualification}</dd></div>}
        {p.professional_qualification && <div><dt className="text-gray-400">Professional</dt><dd>{p.professional_qualification}</dd></div>}
        {p.post_qualification_experience && <div className="sm:col-span-2"><dt className="text-gray-400">Experience</dt><dd>{p.post_qualification_experience}</dd></div>}
        {p.qsrbn_reg_no && <div><dt className="text-gray-400">QSRBN</dt><dd>{p.qsrbn_reg_no}</dd></div>}
      </dl>
    </div>
  );
}

export default function AdminFirmApplicationDetailPage() {
  const { id } = useParams();

  // Reading an application and deciding one are different privileges, and the
  // API enforces that — so the controls for deciding are not rendered to
  // somebody who would only be refused. `firm_applications.access` on its own
  // makes this page read-only. See PERMS.firm / FirmPermissionSeeder.
  const { can } = useAccess();
  const canReview = can.perm(PERMS.firm.review);
  const canIssue  = can.perm(PERMS.firm.issueNumber);

  const { data: app, isLoading, refetch } = useAdminFirmApplicationDetailsQuery(id!, { skip: !id });
  const [update, { isLoading: updating }] = useUpdateFirmApplicationStatusMutation();

  /* Committee assignment. The pool is only fetched where it can be used —
     which includes an application already with the committee, so another
     member can be added to it. */
  // Both: the application must be at a stage where it can be handed over, AND
  // this person must hold the privilege to hand it.
  const assignable =
    canReview && (app?.status === "under_review" || app?.status === "with_committee");
  const { data: pool } = useGetFirmCommitteePoolQuery(undefined, { skip: !assignable });
  const { data: committeeReviews = [] } = useGetFirmCommitteeReviewsQuery(id!, { skip: !id });
  const [assign, { isLoading: assigning }] = useAssignFirmCommitteeMutation();
  const [unassign] = useUnassignFirmCommitteeMutation();
  const [picked, setPicked] = useState<string[]>([]);

  /* The interview close-out: the donation is agreed face-to-face, so nothing
     can be charged until an admin records the figure. Re-openable while it is
     still unpaid, so a mistyped amount can be corrected. */
  const canSetDonation =
    canReview &&
    (app?.status === "ready_for_interview" ||
      (app?.status === "pending_donation" && !app?.donation_paid_at));
  const [setDonation, { isLoading: settingDonation }] = useSetFirmDonationMutation();
  const [donationNaira, setDonationNaira] = useState("");

  /* Issuing the firm number — only once the agreed donation has been paid,
     which is what `donated` means. */
  // Its own privilege, and the narrowest on this page: issuing the number puts
  // the practice on the Institute's register.
  const canIssueNumber = canIssue && app?.status === "donated";
  const { data: nextNumber } = useGetNextFirmNumberQuery(undefined, { skip: !canIssueNumber });
  const [issueNumber, { isLoading: issuing }] = useIssueFirmNumberMutation();
  const [numberMode, setNumberMode] = useState<"auto" | "manual">("auto");
  const [manualNumber, setManualNumber] = useState("");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  if (isLoading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!app) return <div className="p-8 text-center text-gray-400">Not found.</div>;

  const partners = app.partners ?? [];
  const principal = partners.find((p: any) => p.role === "principal");
  const others = partners.filter((p: any) => p.role === "partner");
  const directors = partners.filter((p: any) => p.role === "director");
  const referees = app.referees ?? [];
  const documents = app.documents ?? [];

  const pendingPartners = others.filter((p: any) => p.decision !== "accepted").length;
  const approvedReferees = referees.filter((r: any) => r.decision === "approved").length;
  const chapterEndorsed = !!app.chapter_accepted_at;

  // Returning an application, handing it on — decisions, so they need the
  // review privilege. Without it the Decision panel simply is not there and the
  // page is a read-only view of the file.
  const transitions = canReview ? (TRANSITIONS[app.status] ?? []) : [];

  const doAssign = async () => {
    setError(null); setOk(null);
    try {
      const res: any = await assign({ id: app.id, member_ids: picked }).unwrap();
      setOk(
        res?.status === "with_committee" && app.status === "under_review"
          ? `Handed to ${picked.length} committee member(s) — it now sits under With Committee.`
          : `Assigned to ${picked.length} committee member(s).`,
      );
      setPicked([]);
      refetch();
    } catch (e: any) {
      setError(describeApiError(e, "Could not assign committee members."));
    }
  };

  const doSetDonation = async () => {
    setError(null); setOk(null);
    const amount = Number(donationNaira);
    if (!amount || amount <= 0) {
      setError("Type the donation amount agreed at the interview.");
      return;
    }
    try {
      const res: any = await setDonation({ id: app.id, amount_naira: amount, remark: remark || undefined }).unwrap();
      setOk(res?.message ?? "Donation recorded.");
      setDonationNaira("");
      setRemark("");
      refetch();
    } catch (e: any) {
      setError(describeApiError(e, "Could not record the donation."));
    }
  };

  const doIssueNumber = async () => {
    setError(null); setOk(null);
    const manual = manualNumber.trim();
    if (numberMode === "manual" && !manual) {
      setError("Type the firm number to issue, or switch back to the next number in the series.");
      return;
    }
    const shown = numberMode === "manual" ? manual : nextNumber?.next;
    if (!window.confirm(`Register this firm as ${shown}? A firm number is permanent.`)) return;
    try {
      const res: any = await issueNumber({
        id: app.id,
        firm_no: numberMode === "manual" ? manual : undefined,
      }).unwrap();
      setOk(res?.message ?? "Firm number issued.");
      setManualNumber("");
      refetch();
    } catch (e: any) {
      setError(describeApiError(e, "Could not issue the firm number."));
    }
  };

  const act = async (status: string) => {
    setError(null); setOk(null);
    if (status === "returned" && !remark.trim()) {
      setError("Add a note telling the applicant what to correct before returning it.");
      return;
    }

    try {
      await update({ id: app.id, status, remark: remark || undefined }).unwrap();
      setOk(`Application moved to ${status.replace("_", " ")}.`);
      setRemark("");
      refetch();
    } catch (e: any) {
      setError(describeApiError(e, "Could not update status."));
    }
  };

  return (
    <div className="w-full p-4 md:p-6 max-w-3xl mx-auto">
      <Link to="/admin/firm-applications" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{app.practice_name}</h1>
          <p className="text-xs text-gray-400">
            #{String(app.id).padStart(6, "0")} · {app.user?.name} · {app.user?.email}
          </p>
        </div>
        <FirmStatusBadge status={app.status} />
      </div>

      {app.firm && (
        <div className="mb-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
          Registered firm: <span className="font-medium">{app.firm.name}</span> · firm no.{" "}
          <span className="font-mono font-semibold">{app.firm.firm_no}</span>
        </div>
      )}

      {/* Endorsements — an application moves to under_review on its own once
          these are all in; the admin does not send it. */}
      <div className="bg-white border rounded-xl p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Review prerequisites</h3>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2">{chapterEndorsed ? decisionIcon.accepted : decisionIcon.pending} Chapter endorsement{app.chapter_accept_comment ? ` — ${app.chapter_accept_comment}` : ""}</div>
          {/* Partners are declared on the form, not invited, so this only ever
              has anything to say about a legacy application whose partners were
              picked from the register. */}
          {pendingPartners > 0 && (
            <div className="flex items-center gap-2">{decisionIcon.pending} All partners accepted ({others.length - pendingPartners}/{others.length})</div>
          )}
          <div className="flex items-center gap-2">{approvedReferees >= 2 ? decisionIcon.accepted : decisionIcon.pending} Two referees approved ({approvedReferees}/2)</div>
        </div>
      </div>

      {app.status === "returned" && app.remarks && (
        <div className="mb-3 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800">
          Returned with note: {app.remarks}
        </div>
      )}

      {app.status === "rejected" && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          <p className="font-medium">A committee member returned this to you.</p>
          {app.remarks && <p className="mt-0.5">{app.remarks}</p>}
          <p className="mt-1">Leave it as it stands, or return it to the applicant to correct.</p>
        </div>
      )}

      {app.status === "ready_for_interview" && (
        <div className="mb-3 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-800">
          Approved by the committee — the interview is next. Record the donation agreed at it below.
        </div>
      )}

      {app.status === "pending_donation" && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          Interview done. Waiting on the applicant's donation
          {app.donation_amount != null && <> of ₦{(Number(app.donation_amount) / 100).toLocaleString()}</>}.
        </div>
      )}

      {app.status === "donated" && (
        <div className="mb-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
          Donation of ₦{(Number(app.donation_amount ?? 0) / 100).toLocaleString()} received
          {app.donation_paid_at ? ` on ${fmtDate(app.donation_paid_at)}` : ""}. Issue the firm number to register it.
        </div>
      )}

      {error && <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}</div>}
      {ok && <div className="mb-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">{ok}</div>}

      {transitions.length > 0 && (
        <div className="bg-white border rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Decision</h3>
          <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
            placeholder="Remark (required when returning)"
            className="w-full border rounded-lg px-3 py-2 text-sm mb-3 outline-none focus:ring-2 focus:ring-indigo-200" />
          <div className="flex gap-2 flex-wrap">
            {transitions.map((t) => (
              <button key={t} onClick={() => act(t)} disabled={updating}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${
                  "bg-orange-500 hover:bg-orange-600"
                }`}>
                {updating ? "…" : ACTION_LABEL[t] ?? t}
              </button>
            ))}
          </div>
        </div>
      )}



      {/* The interview close-out — recording the agreed donation is what puts
          the application in front of the applicant to pay. */}
      {canSetDonation && (
        <div className="bg-white border rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-1">
            {app.status === "pending_donation" ? "Correct the agreed donation" : "Record interview & donation"}
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            {app.status === "pending_donation"
              ? "The applicant has not paid yet, so the agreed figure can still be corrected. Doing so cancels any unpaid attempt against the old amount."
              : "Enter the donation agreed at the interview. This is what the applicant is charged, and it is what moves the application to Donation Due."}
          </p>

          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-sm text-gray-700">
              <span className="block text-xs text-gray-500 mb-1">Agreed donation (₦)</span>
              <input
                type="number"
                min={1}
                value={donationNaira}
                onChange={(e) => setDonationNaira(e.target.value)}
                placeholder={
                  app.donation_amount != null
                    ? String(Number(app.donation_amount) / 100)
                    : "e.g. 250000"
                }
                className="w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </label>
            <button
              onClick={doSetDonation}
              disabled={settingDonation}
              className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              {settingDonation ? "Saving…" : "Record donation"}
            </button>
          </div>
        </div>
      )}

      {/* Issuing the firm number — the donation is in, and this registers it. */}
      {canIssueNumber && (
        <div className="bg-white border rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-1">Issue firm number</h3>
          <p className="text-xs text-gray-500 mb-3">
            The interview is done and the agreed donation is paid. Issuing the number registers the
            firm — it is permanent and cannot be undone.
          </p>

          <div className="space-y-2 mb-3">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="radio"
                className="mt-0.5"
                checked={numberMode === "auto"}
                onChange={() => setNumberMode("auto")}
              />
              <span>
                Next in the series
                {nextNumber?.next && (
                  <span className="ml-1 font-mono font-semibold text-gray-900">{nextNumber.next}</span>
                )}
                <span className="block text-xs text-gray-400">
                  Read off the register, so a number issued on paper still advances the series.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="radio"
                className="mt-0.5"
                checked={numberMode === "manual"}
                onChange={() => setNumberMode("manual")}
              />
              <span className="flex-1">
                Enter it myself
                <input
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-50"
                  placeholder={nextNumber?.prefix ? `${nextNumber.prefix}0001` : "Firm number"}
                  value={manualNumber}
                  disabled={numberMode !== "manual"}
                  onChange={(e) => setManualNumber(e.target.value)}
                />
              </span>
            </label>
          </div>

          <button
            onClick={doIssueNumber}
            disabled={issuing}
            className="px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60"
          >
            {issuing ? "Issuing…" : "Issue number & register firm"}
          </button>
        </div>
      )}

      {/* Membership Committee — the admin assigns, the assigned member decides. */}
      {(assignable || committeeReviews.length > 0) && (
        <div className="bg-white border rounded-xl p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-1">Membership Committee</h3>
          <p className="text-xs text-gray-500 mb-3">
            Assigning hands this application over: it leaves Under Review for With Committee. An
            assigned member either approves it — sending it forward for interview — or returns it to
            you with a message. Removing the last assignment brings it back to you.
          </p>

          {committeeReviews.length === 0 && (
            <p className="text-sm text-gray-400 mb-3">Nobody assigned yet.</p>
          )}
          {committeeReviews.map((r: any) => (
            <div key={r.id} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
              <div className="min-w-0">
                <p className="text-sm text-gray-900">{r.name}</p>
                <p className="text-xs text-gray-400">
                  {r.decision === "pending" ? "Awaiting their decision" : `${r.decision}${r.decided_at ? ` · ${r.decided_at}` : ""}`}
                </p>
                {r.remark && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{r.remark}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {decisionIcon[r.decision]}
                {r.decision === "pending" && (
                  <button
                    onClick={async () => { await unassign({ id: app.id, reviewId: r.id }); refetch(); }}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}

          {assignable && (
            <div className="mt-3 border-t pt-3">
              {!pool?.committee && (
                <p className="text-sm text-amber-700">
                  No active Membership Committee was found, so nobody can be assigned yet.
                </p>
              )}
              {pool?.committee && (
                <>
                  <p className="text-xs text-gray-400 mb-2">{pool.committee.name}</p>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {(pool.members ?? [])
                      .filter((m: any) => !committeeReviews.some((r: any) => r.member_id === m.member_id))
                      .map((m: any) => (
                        <label key={m.member_id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={picked.includes(m.member_id)}
                            onChange={(e) =>
                              setPicked(e.target.checked
                                ? [...picked, m.member_id]
                                : picked.filter((x) => x !== m.member_id))}
                          />
                          <span>{m.name}</span>
                          <span className="text-xs text-gray-400">{m.membership_no} · {m.committee_role}</span>
                        </label>
                      ))}
                  </div>
                  <button
                    onClick={doAssign}
                    disabled={assigning || picked.length === 0}
                    className="mt-3 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {assigning ? "Assigning…" : `Assign ${picked.length || ""}`.trim()}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Firm details */}
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Firm details</h3>
        <Row label="Nature of practice" value={app.nature_of_practice} />
        <Row label="Registered address" value={app.registered_address} />
        <Row label="Firm email" value={app.email} />
        <Row label="Firm phone number" value={app.phone} />
        <Row label="State" value={app.state?.name ?? (typeof app.state === "string" ? app.state : null)} />
      </div>

      {/* CAMA / Business Name registration particulars */}
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">
          Registration under the Companies and Allied Matters Act or the Registration of Business Name Act
        </h3>
        <Row label="CAC registration number" value={app.cama_reg_number} />
        <Row label="Date of registration with CAC" value={fmtDate(app.cac_reg_date)} />
        <Row label="Date of commencement of practice" value={fmtDate(app.commencement_date)} />
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Ownership structure</h3>
        <Row label="Structure" value={app.ownership_structure} />
        <Row label="Number of partners / directors" value={app.num_partners} />
        {(app.affiliations ?? []).length > 0 && (
          <div className="pt-2">
            <p className="text-sm text-gray-500 mb-1">Affiliations in Nigeria or abroad</p>
            <ul className="text-sm text-gray-900 space-y-0.5">
              {(app.affiliations ?? []).map((a: string, i: number) => <li key={i}>• {a}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Directors — declared on step 1 alongside the ownership structure. */}
      {directors.length > 0 && (
        <div className="bg-white border rounded-xl p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-1">Directors</h3>
          {directors.map((d: any) => <PartnerCard key={d.id} p={d} />)}
        </div>
      )}

      {/* Partners */}
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-1">Partners</h3>
        {principal && <PartnerCard p={principal} principal />}
        {others.length === 0 && <p className="text-sm text-gray-400 mt-2">Sole practice — no additional partners.</p>}
        {others.map((p: any) => <PartnerCard key={p.id} p={p} />)}
      </div>

      {/* Referees */}
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Referees</h3>
        {referees.length === 0 && <p className="text-sm text-gray-400">None.</p>}
        {referees.map((r: any) => (
          <div key={r.id} className="flex items-center justify-between py-2 border-b last:border-0">
            <div>
              <p className="text-sm text-gray-900">{r.member?.surname ?? r.member_id}</p>
              <p className="text-xs text-gray-400">{r.member?.membership_no}</p>
            </div>
            {decisionIcon[r.decision]}
          </div>
        ))}
      </div>

      {/* Benevolence */}
      {(app.benevolence_records ?? []).length > 0 && (
        <div className="bg-white border rounded-xl p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Acts of benevolence to NIQS</h3>
          {(app.benevolence_records ?? []).map((b: any) => (
            <div key={b.id} className="py-2 border-b last:border-0">
              {b.type && <p className="text-xs font-medium text-indigo-600">{b.type}</p>}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{b.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Documents */}
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Supporting documents</h3>
        {documents.length === 0 && <p className="text-sm text-gray-400">None uploaded.</p>}
        {documents.map((d: any) => {
          const url = firmStorageUrl(d.file_path);
          return (
            <div key={d.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <p className="text-sm text-gray-900">{d.doc_type}</p>
                <p className="text-xs text-gray-400">{d.file_name}</p>
              </div>
              {url && <a href={url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline">View</a>}
            </div>
          );
        })}
      </div>

      {/* Chapter + payment */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-2">Chapter & payment</h3>
        {/* The applicant never picked this — step 7 stamps their own chapter. */}
        <Row label="Chapter" value={app.chapter?.name} />
        <Row label="Chairman" value={app.chapter_chairman_name} />
        <Row label="Chairman email" value={app.chapter_chairman_email} />
        <Row label="Secretary" value={app.chapter_secretary_name} />
        <Row label="Secretary email" value={app.chapter_secretary_email} />
        <Row label="Declaration acknowledged" value={fmtDate(app.acknowledged_at)} />
        <Row label="Payment status" value={app.payment?.status} />
        <Row label="Payment reference" value={app.reference} />
        <Row label="Invoice no." value={app.invoice?.invoice_number} />
        <Row label="Receipt no." value={app.payment?.receipt_number} />
        <Row label="Amount" value={app.amount != null ? `₦${(Number(app.amount) / 100).toLocaleString()}` : null} />
      </div>
    </div>
  );
}
